import { createElement, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { AlertTriangle } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { usePageLayout } from '@/hooks/usePageLayout'
import { Button } from '@/components/ui/button'
import SettingsSidebar from '@/components/app/sidebars/settings'
import {
  SettingsCard,
  SettingsPage,
  SettingsRow,
  SettingsSection,
} from '@/components/app/settings'
import { PushTestSection } from '@/routes/features/settings/notifications/components/PushTestSection'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { resetAllLocalData } from '@/lib/db/resetUtils'

export default function DatabaseSettingsPage() {
  const { t } = useTranslation('settings')
  const { t: tCommon } = useTranslation('common')
  const [showResetDialog, setShowResetDialog] = useState(false)
  const [isResetting, setIsResetting] = useState(false)

  usePageLayout({
    sidebarContent: createElement(SettingsSidebar),
    showHeader: false,
  })

  const handleResetDatabase = async () => {
    setIsResetting(true)
    try {
      await resetAllLocalData()
      // Page will reload automatically after reset
    } catch (error) {
      toast.error(t('databasePage.toast.resetFailed'))
      setIsResetting(false)
      setShowResetDialog(false)
    }
  }

  return (
    <SettingsPage title={t('databasePage.title')}>
      <SettingsSection
        title={t('databasePage.sectionTitle')}
        description={t('databasePage.sectionDescription')}
      >
        <SettingsCard>
          <SettingsRow
            label={
              <span className="flex items-center gap-1.5">
                <AlertTriangle className="size-3.5 text-destructive" />
                {t('databasePage.resetCard.title')}
              </span>
            }
            description={t('databasePage.resetCard.description')}
          >
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowResetDialog(true)}
              disabled={isResetting}
              className="shrink-0 text-destructive hover:text-destructive border-destructive/30 hover:bg-destructive/10"
            >
              {t('databasePage.resetCard.button')}
            </Button>
          </SettingsRow>
        </SettingsCard>
      </SettingsSection>

      {/* Dev-only push sender (moved here from Notifications). */}
      <PushTestSection />

      {/* Reset Database Confirmation Dialog */}
      <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-base">
              <AlertTriangle className="size-4 text-destructive" />
              {t('databasePage.resetDialog.title')}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3 text-sm">
              <p>
                {t('databasePage.resetDialog.warning')}
              </p>
              <p className="text-foreground/80">
                {t('databasePage.resetDialog.warningSecondary')}
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isResetting}>{tCommon('actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleResetDatabase}
              disabled={isResetting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isResetting ? t('databasePage.resetDialog.resetting') : t('databasePage.resetDialog.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SettingsPage>
  )
}

export const Route = createFileRoute(
  '/_AppWrapper/_AppLayout/$workspaceSlug/settings/database',
)({
  component: DatabaseSettingsPage,
})
